import { ActionType, formatCurrency, UserId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { PageHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { DetailPageLayout } from "components/shared/detail-page-layout";
import { CrmControlledTable } from "components/table/crm-controlled-table";
import { CrmTableColumn } from "components/table/types";
import { Badge } from "components/ui/badge";
import { ErrorMessage } from "components/ui/error-message";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryCompactButton } from "components/ui/primary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { useGetVisibleUsersAcrossRegionsQuery } from "generated/graphql";
import React, { useCallback } from "react";
import { Link, Redirect } from "react-router-dom";
import { useFeature, useIsAllowedInWholeTenant } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { stableQueryOptions } from "util/requests";

type CommissionReportRow =
    inferRouterOutputs<AppRouter>["commissionReport"]["listReport"]["rows"][number];

const PAGE_SIZE = 500;
const FETCHING_INDICATOR_DELAY_MS = 500;

const useDelayedFlag = (active: boolean, delayMs: number): boolean => {
    const [shown, setShown] = React.useState(false);
    React.useEffect(() => {
        if (!active) {
            setShown(false);
            return undefined;
        }
        const timer = setTimeout(() => setShown(true), delayMs);
        return () => clearTimeout(timer);
    }, [active, delayMs]);
    return shown;
};

const statusBadgeType = (
    status: CommissionReportRow["status"],
): "error" | "info" | "highlight" | "success" => {
    switch (status) {
        case "Paid":
            return "success";
        case "Owed":
            return "highlight";
        case "Pending":
            return "info";
    }
};

const CommissionReportTable: React.FC = () => {
    const trpcClient = trpc.useTRPC();
    const [creditorUserId, setCreditorUserId] = React.useState<UserId | "">("");
    const [currentPage, setCurrentPage] = React.useState(1);
    React.useEffect(() => {
        setCurrentPage(1);
    }, [creditorUserId]);
    const usersQuery = useGetVisibleUsersAcrossRegionsQuery(
        undefined,
        stableQueryOptions(),
    );
    const queryClient = useQueryClient();
    const reportQueryOptions = trpcClient.commissionReport.listReport.queryOptions({
        creditorUserId: creditorUserId === "" ? null : creditorUserId,
        page: currentPage,
        perPage: PAGE_SIZE,
    });
    const reportQuery = useQuery({
        ...reportQueryOptions,
        placeholderData: keepPreviousData,
    });
    const showFetchingIndicator = useDelayedFlag(
        reportQuery.isFetching,
        FETCHING_INDICATOR_DELAY_MS,
    );

    const replaceRow = useCallback(
        (updatedRow: CommissionReportRow) => {
            queryClient.setQueryData(reportQueryOptions.queryKey, previous =>
                previous === undefined
                    ? previous
                    : {
                          ...previous,
                          rows: previous.rows.map(row =>
                              row.accrualId === updatedRow.accrualId ? updatedRow : row,
                          ),
                      },
            );
        },
        [queryClient, reportQueryOptions.queryKey],
    );

    const markPaidMutation = useMutation({
        ...trpcClient.commissionReport.markPaid.mutationOptions(),
        onSuccess: data => replaceRow(data.row),
    });
    const undoMutation = useMutation({
        ...trpcClient.commissionReport.undo.mutationOptions(),
        onSuccess: () => reportQuery.refetch(),
    });

    const isMutating = markPaidMutation.isPending || undoMutation.isPending;

    const markPaid = useCallback(
        (accrualId: string) => markPaidMutation.mutate({ accrualId }),
        [markPaidMutation],
    );
    const undo = useCallback(
        async (accrualId: string) => {
            const confirmed = await fancyConfirm(
                "Undo Marked Paid?",
                "This will revert the commission to unpaid, and its amount and status " +
                    "will be recalculated based on the invoice's current payment status. " +
                    "Are you sure?",
                "Yes, undo",
                "Cancel",
                { confirmStyle: "question" },
            );
            if (confirmed) {
                undoMutation.mutate({ accrualId });
            }
        },
        [undoMutation],
    );

    const columns: CrmTableColumn<CommissionReportRow>[] = React.useMemo(
        () => [
            {
                key: "creditorUserName",
                header: "Team member",
                width: 160,
                renderCell: ({ record }) =>
                    record ? (
                        <div className="text-sm text-gray-900">
                            {record.creditorUserName}
                        </div>
                    ) : (
                        <span />
                    ),
            },
            {
                key: "invoiceSerial",
                header: "Invoice",
                renderCell: ({ record }) => {
                    if (!record) {
                        return <span />;
                    }
                    const label = `${
                        record.clientName || record.communityName || "Unknown"
                    } (#${record.invoiceSerial})`;
                    return record.invoiceAppLink ? (
                        <Link
                            to={record.invoiceAppLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={defaultLinkStyle}>
                            {label}
                        </Link>
                    ) : (
                        <div className="text-sm text-gray-900">{label}</div>
                    );
                },
            },
            {
                key: "rateBasisPoints",
                header: "Rate",
                width: 90,
                renderCell: ({ record }) =>
                    record ? (
                        <div className="text-sm text-gray-500">
                            {(record.rateBasisPoints / 100).toFixed(2)}%
                        </div>
                    ) : (
                        <span />
                    ),
            },
            {
                key: "amountCents",
                header: "Amount",
                width: 120,
                renderCell: ({ record }) =>
                    record ? (
                        <div className="text-sm text-gray-900">
                            {record.amountCents === null
                                ? "—"
                                : formatCurrency(record.amountCents / 100)}
                        </div>
                    ) : (
                        <span />
                    ),
            },
            {
                key: "status",
                header: "Status",
                width: 100,
                renderCell: ({ record }) =>
                    record ? (
                        <Badge
                            type={statusBadgeType(record.status)}
                            value={record.status}
                        />
                    ) : (
                        <span />
                    ),
            },
            {
                key: "accrualId",
                header: "Action",
                width: 140,
                renderCell: ({ record }) => {
                    if (!record || !record.accrualId) {
                        return <span className="text-sm text-gray-400">—</span>;
                    }
                    if (record.status === "Owed") {
                        const accrualId = record.accrualId;
                        return (
                            <PrimaryCompactButton
                                disabled={isMutating}
                                onClick={() => markPaid(accrualId)}>
                                Mark paid
                            </PrimaryCompactButton>
                        );
                    }
                    if (record.status === "Paid") {
                        const accrualId = record.accrualId;
                        return (
                            <PrimaryCompactButton
                                disabled={isMutating}
                                onClick={() => undo(accrualId)}>
                                Undo
                            </PrimaryCompactButton>
                        );
                    }
                    return <span className="text-sm text-gray-400">—</span>;
                },
            },
        ],
        [isMutating, markPaid, undo],
    );

    if (reportQuery.isLoading) {
        return (
            <div className="bg-white rounded border border-gray-200 p-4">
                <Spinner />
            </div>
        );
    }

    if (reportQuery.isError) {
        return (
            <div className="bg-white rounded border border-gray-200 p-4">
                <ErrorMessage component="CommissionReport">
                    {reportQuery.error.message}
                </ErrorMessage>
            </div>
        );
    }

    const actionError = markPaidMutation.error ?? undoMutation.error;

    const sortedUsers = (usersQuery.data?.getVisibleUsersAcrossRegions ?? [])
        .filter(u => !u.hidden)
        .slice()
        .sort((a, b) => a.preferredName.localeCompare(b.preferredName));

    return (
        <div className="space-y-2">
            {actionError ? (
                <ErrorMessage component="CommissionReport">
                    {actionError.message}
                </ErrorMessage>
            ) : null}
            <div className="flex items-center gap-3">
                <div className="w-64">
                    <Select
                        label="Team member"
                        value={creditorUserId}
                        onChange={e => setCreditorUserId(e.target.value as UserId | "")}>
                        <option value="">All team members</option>
                        {sortedUsers.map(u => (
                            <option key={u.id} value={u.id}>
                                {u.preferredName}
                            </option>
                        ))}
                    </Select>
                </div>
                {showFetchingIndicator ? <Spinner /> : null}
            </div>
            <div
                className={`bg-white rounded border border-gray-200 ${
                    showFetchingIndicator ? "opacity-60 transition-opacity" : ""
                }`}>
                <CrmControlledTable
                    data={reportQuery.data?.rows ?? []}
                    columns={columns}
                    rowCount={reportQuery.data?.total ?? 0}
                    currentPage={currentPage}
                    goToPage={setCurrentPage}
                    pageSize={PAGE_SIZE}
                    getRowId={row => row.key}
                    scrollContainerClassName="max-h-[calc(100vh-260px)]"
                    itemLabel="commissions"
                />
            </div>
        </div>
    );
};

export const CommissionReport: React.FC<unknown> = () => {
    const featureEnabled = useFeature("employeeCommissions");
    const { settings } = useTenantSettings();
    const canManage = useIsAllowedInWholeTenant(ActionType.ManageEmployeeCommissions);

    if (!featureEnabled || !settings.employeeCommissionsEnabled || !canManage) {
        return <Redirect to="/tasks" />;
    }

    return (
        <div>
            <PageHeader title="Commission Report" />
            <DetailPageLayout>
                <div className="pt-2">
                    <CommissionReportTable />
                </div>
            </DetailPageLayout>
        </div>
    );
};
